import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unionBy } from 'lodash';
import { TaskItem, TodoIntegrationType } from '@demind-inc/core';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { AxiosError } from 'axios';

import { TaskError401Res, tasksApi } from '../api';
import { TasksByDate, UseUpdateTodoTaskParams } from '../types';
import { taskErrorSnackBarAtom } from '../recoil';
import { getDueDateYYYYMMDD, getIsOverdue } from '../../helpers';
import { TEAM_TASKS_QUERY_KEY } from '../queries/teamTasks.shared';
import { translateI18n } from '../../i18n';

export const useUpdateTodoTask = () => {
  const queryClient = useQueryClient();
  const [_, setTaskErrorSnackbar] = useRecoilState(taskErrorSnackBarAtom);

  const updateTaskMutation = useMutation({
    mutationFn: ({ boardId, taskId, userId, newTaskInfo }: UseUpdateTodoTaskParams) => {
      return tasksApi.updateTodoTask(taskId, boardId, userId, newTaskInfo).then(({ data }) => data);
    },
    onMutate: async ({ taskId, newTaskInfo, movedTaskParam }) => {
      const dueDate = getDueDateYYYYMMDD(newTaskInfo.dueDateTime);

      if (!dueDate || getIsOverdue(dueDate)) {
        // If the due date is not set or is overdue, update the overdue tasks
        await queryClient.cancelQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
        queryClient.setQueriesData<TaskItem[]>(
          { queryKey: ['lifestack.todo.overdueTasks'] },
          (prevTasks) =>
            prevTasks?.map((task) => (task.taskId === taskId ? { ...task, ...newTaskInfo } : task))
        );
        return;
      }
      await queryClient.cancelQueries({
        queryKey: dueDate ? ['lifestack.todo.tasks', { dueDate }] : ['lifestack.todo.tasks'],
      });
      queryClient.setQueriesData<TasksByDate>(
        { queryKey: ['lifestack.todo.tasks'] },
        (prevTasks) => {
          const prevTaskDate = prevTasks?.date;

          let updatedTasks = prevTasks?.tasks?.map((task) =>
            task.taskId === taskId ? { ...task, ...newTaskInfo } : task
          );

          // Update the state of the task when moving between dates
          if (!!movedTaskParam && movedTaskParam.prevDate === prevTaskDate) {
            updatedTasks = updatedTasks?.filter((task) => task.taskId !== taskId);
          }
          if (!!movedTaskParam && movedTaskParam.newDate === prevTaskDate) {
            const addedTask = { ...newTaskInfo, taskId } as TaskItem;
            updatedTasks = unionBy(updatedTasks, [addedTask], 'taskId');
          }

          return {
            date: prevTaskDate,
            tasks: updatedTasks,
            updatedAt: new Date().toISOString(),
          };
        }
      );

      // Update overdue tasks
      if (movedTaskParam?.wasOverdue || !movedTaskParam?.prevDate) {
        queryClient.setQueriesData<TaskItem[]>(
          { queryKey: ['lifestack.todo.overdueTasks'] },
          (prevTasks) => prevTasks?.filter((task) => task.taskId !== taskId)
        );
      }
    },
    onSuccess: (_, { newTaskInfo, movedTaskParam }) => {
      const dueDate = getDueDateYYYYMMDD(newTaskInfo.dueDateTime);

      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY, type: 'active' });

      if (getIsOverdue(dueDate)) {
        queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
        queryClient.invalidateQueries({
          queryKey: ['lifestack.todo.tasks', { dueDate: movedTaskParam?.prevDate }],
        });
        return;
      }

      if (movedTaskParam) {
        queryClient.invalidateQueries({
          queryKey: ['lifestack.todo.tasks', { dueDate: movedTaskParam.prevDate }],
        });
        if (movedTaskParam.prevDate !== movedTaskParam.newDate) {
          queryClient.invalidateQueries({
            queryKey: ['lifestack.todo.tasks', { dueDate: movedTaskParam.newDate }],
          });
        }
        if (movedTaskParam.wasOverdue || !movedTaskParam.prevDate) {
          queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
        }
        return;
      } else {
        queryClient.invalidateQueries({
          queryKey: dueDate ? ['lifestack.todo.tasks', { dueDate }] : ['lifestack.todo.tasks'],
        });
        queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
      }
    },
  });

  useEffect(() => {
    const error = (updateTaskMutation.error as AxiosError)?.response;
    if (!error) {
      return;
    }
    if (error.status === 401) {
      setTaskErrorSnackbar({
        status: error.status,
        message: translateI18n('task.error.invalidToken'),
        provider: (error.data as TaskError401Res).provider as TodoIntegrationType,
        action: 'update',
      });
      return;
    }
    setTaskErrorSnackbar({
      status: error.status,
      message: translateI18n('task.error.updateFailed'),
      provider: (error.data as TaskError401Res).provider as TodoIntegrationType,
      action: 'update',
    });
  }, [updateTaskMutation.error]);

  return {
    updateTodoTask: updateTaskMutation.mutateAsync,
    isUpdating: updateTaskMutation.isPending,
    ...updateTaskMutation,
  };
};
